import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Search, ChevronDown, X, Check } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Skeleton } from "@/components/ui/skeleton";

interface ItemOption {
  item_id: string | null;
  item_nome: string;
  image_url: string | null;
}

interface ItemSearchComboboxProps {
  anuncios: Array<{ item_id: string | null; item_nome: string }>;
  itemDetails: Record<string, any> | undefined;
  selected: string[];
  onChange: (items: string[]) => void;
  onOpenAnuncio?: (id: string) => void;
}

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

const ItemSearchCombobox = ({ anuncios, itemDetails, selected, onChange, onOpenAnuncio }: ItemSearchComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Close on outside click (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isMobile]);

  // Lock body scroll on mobile when open
  useEffect(() => {
    if (isMobile && open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isMobile, open]);

  const options = useMemo(() => {
    const map = new Map<string, ItemOption>();
    for (const a of anuncios || []) {
      const key = a.item_nome.toLowerCase();
      if (map.has(key)) continue;
      const details = a.item_id && itemDetails ? itemDetails[a.item_id] : null;
      map.set(key, {
        item_id: a.item_id,
        item_nome: a.item_nome,
        image_url: details?.image_url || null,
      });
    }
    return Array.from(map.values()).sort((a, b) => a.item_nome.localeCompare(b.item_nome, "pt-BR"));
  }, [anuncios, itemDetails]);

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filtered = useMemo(() => {
    if (!debouncedSearch.trim()) return options;
    const s = debouncedSearch.toLowerCase();
    const matches = options.filter((o) => o.item_nome.toLowerCase().includes(s));
    matches.sort((a, b) => {
      const aStarts = a.item_nome.toLowerCase().startsWith(s) ? 0 : 1;
      const bStarts = b.item_nome.toLowerCase().startsWith(s) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      return a.item_nome.localeCompare(b.item_nome, "pt-BR");
    });
    return matches;
  }, [options, debouncedSearch]);

  const toggle = (nome: string) => {
    onChange(selected.includes(nome) ? selected.filter((s) => s !== nome) : [...selected, nome]);
  };

  const removeItem = (nome: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((s) => s !== nome));
  };

  const handleOpen = () => {
    if (open) {
      setOpen(false);
      setSearch("");
      return;
    }
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const dropdownContent = (
    <>
      {/* Search input */}
      <div className="px-3 py-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar item..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent font-pixel text-[8px] sm:text-[7px] text-foreground outline-none placeholder:text-muted-foreground/60"
          />
          {search && (
            <button onClick={() => setSearch("")} className="cursor-pointer">
              <X className="w-3 h-3 text-muted-foreground/60" />
            </button>
          )}
        </div>
      </div>

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="px-3 py-1.5 border-b border-border/40 flex flex-wrap gap-1">
          {selected.map((s) => (
            <span
              key={s}
              className="flex items-center gap-0.5 bg-primary/20 text-primary px-1.5 py-0.5 rounded-sm font-pixel text-[7px] whitespace-nowrap"
            >
              {s.length > 16 ? s.slice(0, 16) + "…" : s}
              <X className="w-2.5 h-2.5 cursor-pointer hover:text-destructive" onClick={(e) => removeItem(s, e)} />
            </span>
          ))}
        </div>
      )}

      {/* Items list */}
      <div className={isMobile ? "flex-1 overflow-auto" : "max-h-[240px] overflow-auto"}>
        {filtered.length === 0 ? (
          <div className="px-3 py-4 font-pixel text-[8px] sm:text-[7px] text-muted-foreground text-center">
            Nenhum item encontrado
          </div>
        ) : (
          filtered.map((opt) => {
            const active = selected.includes(opt.item_nome);
            return (
              <div
                key={opt.item_nome}
                className="w-full flex items-center gap-2 px-3 py-2 sm:py-1.5 font-pixel text-[8px] sm:text-[7px] hover:bg-primary/10 active:bg-primary/20 transition-colors text-left"
              >
                <button
                  onClick={() => toggle(opt.item_nome)}
                  className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                >
                  <div
                    className={`w-4 h-4 sm:w-3 sm:h-3 border rounded-sm flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary" : "border-muted-foreground/40"}`}
                  >
                    {active && <Check className="w-2.5 h-2.5 sm:w-2 sm:h-2 text-primary-foreground" />}
                  </div>
                  {loadingImages.has(opt.item_nome) ? (
                    <Skeleton className="w-6 h-6 sm:w-5 sm:h-5 shrink-0" />
                  ) : (
                    <img
                      src={getItemImageUrl(opt.item_nome)}
                      alt=""
                      className="w-6 h-6 sm:w-5 sm:h-5 object-contain image-rendering-pixelated shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/placeholder.svg";
                      }}
                      onLoadStart={() => setLoadingImages((prev) => new Set(prev).add(opt.item_nome))}
                      onLoad={() =>
                        setLoadingImages((prev) => {
                          const next = new Set(prev);
                          next.delete(opt.item_nome);
                          return next;
                        })
                      }
                      loading="lazy"
                    />
                  )}
                  <span className={`flex-1 truncate ${active ? "text-primary" : "text-muted-foreground"}`}>
                    {opt.item_nome}
                  </span>
                </button>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <>
      <div ref={ref} className="relative flex flex-col gap-0.5">
        <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
          <Search className="w-2.5 h-2.5" /> Item
        </label>
        <button
          onClick={handleOpen}
          className="flex items-center gap-1 px-2 h-8 sm:h-9 tibia-border bg-card rounded-sm font-pixel text-[7px] text-muted-foreground cursor-pointer w-full appearance-none"
        >
          <div className="flex-1 flex items-center gap-1 overflow-hidden">
            {selected.length === 0 ? (
              <span className="text-muted-foreground/60">Todos os itens</span>
            ) : (
              <div className="flex items-center gap-0.5 overflow-hidden">
                {selected.slice(0, 1).map((s) => (
                  <span
                    key={s}
                    className="flex items-center gap-0.5 bg-primary/20 text-primary px-1 rounded-sm text-[6px] whitespace-nowrap"
                  >
                    {s.length > 12 ? s.slice(0, 12) + "…" : s}
                    <X className="w-2 h-2 cursor-pointer hover:text-destructive" onClick={(e) => removeItem(s, e)} />
                  </span>
                ))}
                {selected.length > 1 && <span className="text-[6px] text-primary">+{selected.length - 1}</span>}
              </div>
            )}
          </div>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {/* Desktop dropdown */}
        {open && !isMobile && (
          <div className="absolute top-full left-0 mt-1 z-[100] tibia-border bg-card rounded-sm py-1 w-full sm:min-w-[560px] max-w-[720px] shadow-lg">
            {dropdownContent}
          </div>
        )}
      </div>

      {/* Mobile fullscreen overlay */}
      {open && isMobile && (
        <div className="fixed inset-0 z-[100] bg-background flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/40">
            <span className="font-pixel text-[9px] text-foreground">Selecionar Item</span>
            <button
              onClick={() => {
                setOpen(false);
                setSearch("");
              }}
              className="font-pixel text-[8px] text-primary cursor-pointer px-2 py-1"
            >
              Fechar
            </button>
          </div>
          {dropdownContent}
        </div>
      )}

    </>
  );
};

export default ItemSearchCombobox;
